/**
 * Loads, edits, and saves the NYCBS SFTP-push config document at the fixed path
 * `api_config/pharmacy/nycbs_sftp_push/latest`. Surfaces each editable section as
 * UI-friendly state (column rows, response-status rows, query trees, scalars) and
 * rebuilds the full document on save — spreading the original doc first so unknown
 * keys and `_comment`s round-trip untouched.
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../../api/firebase/references";
import { createEmptyGroup } from "../../../../../components/queryBuilder/queryBuilderUtils";
import {
  createEmptyNycbsSftpPushDoc,
  NycbsSftpPushDoc,
} from "../../../../../data-model/nycbsSftpPushConfig";
import { QueryGroup } from "../../../../../data-model/queryBuilder";
import {
  clausesToQueryGroup,
  queryGroupToClauses,
} from "./nycbsSftpPushConversion";
import {
  ColumnMappingRow,
  columnsToRows,
  mappingToRows,
  ResponseStatusRow,
  rowsToColumns,
  rowsToMapping,
} from "./nycbsSftpPushRows";

const COLLECTION_PATH = "api_config/pharmacy/nycbs_sftp_push";
const DOC_ID = "latest";

interface StatusUpdateGroupForm {
  id: string;
  result: string;
  query: QueryGroup;
  _comment?: string;
}

interface SftpPushForm {
  columns: ColumnMappingRow[];
  mappingRows: ResponseStatusRow[];
  fetchFilter: QueryGroup;
  statusUpdates: StatusUpdateGroupForm[];
  filenamePrefix: string;
  validCmmStatuses: string[];
}

const serialize = (form: SftpPushForm): string => JSON.stringify(form);

function formFromDoc(doc: NycbsSftpPushDoc): SftpPushForm {
  return {
    columns: columnsToRows(doc.csv.columns, doc.csv.column_header_mapping),
    mappingRows: mappingToRows(doc.csv.response_status_mapping),
    fetchFilter: clausesToQueryGroup(
      doc.resubmit.fetch_filter?.operator,
      doc.resubmit.fetch_filter?.clauses,
      "column",
    ),
    statusUpdates: (doc.resubmit.status_update_conditions ?? []).map(
      (cond) => ({
        id: uuidv4(),
        result: cond.result ?? "",
        query: clausesToQueryGroup(cond.operator, cond.clauses, "column"),
        _comment: cond._comment,
      }),
    ),
    filenamePrefix: doc.resubmit.filename_prefix ?? "",
    validCmmStatuses: [...(doc.validation.valid_cmm_statuses ?? [])],
  };
}

function docFromForm(
  original: NycbsSftpPushDoc,
  form: SftpPushForm,
): NycbsSftpPushDoc {
  const { columns, column_header_mapping } = rowsToColumns(form.columns);
  return {
    ...original,
    csv: {
      ...original.csv,
      columns,
      column_header_mapping,
      response_status_mapping: rowsToMapping(form.mappingRows),
    },
    validation: {
      ...original.validation,
      valid_cmm_statuses: form.validCmmStatuses
        .map((s) => s.trim())
        .filter((s) => s !== ""),
    },
    resubmit: {
      ...original.resubmit,
      filename_prefix: form.filenamePrefix,
      fetch_filter: queryGroupToClauses(form.fetchFilter, "column", true),
      status_update_conditions: form.statusUpdates.map((g) => {
        const { operator, clauses } = queryGroupToClauses(
          g.query,
          "column",
          true,
        );
        return {
          operator,
          result: g.result,
          clauses,
          ...(g._comment ? { _comment: g._comment } : {}),
        };
      }),
    },
  };
}

/**
 * Hook driving the SFTP Push tab.
 *
 * @return Editable form state, flags, mutators, and save/discard handlers
 */
export function useNycbsSftpPushConfig() {
  const [originalDoc, setOriginalDoc] = useState<NycbsSftpPushDoc>(
    createEmptyNycbsSftpPushDoc,
  );
  const [form, setForm] = useState<SftpPushForm>(() =>
    formFromDoc(createEmptyNycbsSftpPushDoc()),
  );
  const [savedSnapshot, setSavedSnapshot] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const path = FirestoreDocumentReference.nycbsSftpPushConfig();
      const fetched =
        await FirestoreService.getDocumentOrNull<NycbsSftpPushDoc>(path);
      const doc = {
        ...createEmptyNycbsSftpPushDoc(),
        ...fetched,
      } as NycbsSftpPushDoc;
      setOriginalDoc(doc);
      const initial = formFromDoc(doc);
      setForm(initial);
      setSavedSnapshot(serialize(initial));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const isDirty = useMemo(
    () => serialize(form) !== savedSnapshot,
    [form, savedSnapshot],
  );

  const update = useCallback(
    (patch: Partial<SftpPushForm>) => setForm((f) => ({ ...f, ...patch })),
    [],
  );

  const addColumn = useCallback(
    () =>
      setForm((f) => ({
        ...f,
        columns: [
          ...f.columns,
          { id: uuidv4(), bqColumn: "", finalHeader: "" },
        ],
      })),
    [],
  );

  const addMappingRow = useCallback(
    () =>
      setForm((f) => ({
        ...f,
        mappingRows: [
          ...f.mappingRows,
          { id: uuidv4(), secondStp: "", statuses: [], code: "" },
        ],
      })),
    [],
  );

  const addStatusUpdate = useCallback(
    () =>
      setForm((f) => ({
        ...f,
        statusUpdates: [
          ...f.statusUpdates,
          { id: uuidv4(), result: "", query: createEmptyGroup() },
        ],
      })),
    [],
  );

  const handleDiscard = useCallback(() => {
    setForm(formFromDoc(originalDoc));
  }, [originalDoc]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const nextDoc = JSON.parse(
        JSON.stringify(docFromForm(originalDoc, form)),
      ) as NycbsSftpPushDoc;
      await FirestoreService.setDocument(COLLECTION_PATH, DOC_ID, nextDoc);
      setOriginalDoc(nextDoc);
      setSavedSnapshot(serialize(form));
      toast.success("SFTP push configuration saved.");
    } catch {
      toast.error("Failed to save SFTP push configuration.");
    } finally {
      setSaving(false);
    }
  }, [originalDoc, form]);

  return {
    form,
    loading,
    saving,
    error,
    isDirty,
    update,
    addColumn,
    addMappingRow,
    addStatusUpdate,
    handleDiscard,
    handleSave,
  };
}

export type { SftpPushForm, StatusUpdateGroupForm };
